import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { BsThreeDotsVertical } from 'react-icons/bs'
import * as normalActions from '../store/actions'
import { readPlayerPrefs, writePlayerPrefs } from '../utils/playerPrefs'
import { addToPlaylist, createPlaylist, getPlaylists, playlistHas, removeFromPlaylist } from '../utils/playlists'
import { useAuthGate } from './authPrompt'

const QUALITY = ['auto', 1080, 720, 480, 360]
const qualityLabel = (value) => (value === 'auto' || value == null ? 'Auto' : `${value}p`)

const WatchMoreMenu = ({ item, onHide, onImmersive, onContent }) => {
  const dispatch = useDispatch()
  const userId = useSelector(state => state.authReduse.id)
  const { requireAuth, promptEl } = useAuthGate()
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState('off')
  const closeTimer = React.useRef(0)
  const [view, setView] = useState('root')
  const [reason, setReason] = useState('')
  const [prefs, setPrefs] = useState(readPlayerPrefs())
  const [lists, setLists] = useState([])
  const [name, setName] = useState('')
  const [note, setNote] = useState('')

  const reset = () => {
    setView('root')
    setReason('')
    setNote('')
    setName('')
  }

  const close = () => {
    setPhase('out')
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => {
      setOpen(false)
      setPhase('off')
      reset()
    }, 340)
  }

  const openMenu = () => {
    window.clearTimeout(closeTimer.current)
    setOpen(true)
    setPhase('enter')
    reset()
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setPhase('in'))
    })
  }

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => { if (event.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(closeTimer.current)
    }
  }, [open])

  const savePref = (next) => setPrefs(writePlayerPrefs(next))

  const openSave = () => {
    if (!requireAuth()) return
    setLists(getPlaylists(userId))
    setName('')
    setNote('')
    setView('playlist')
  }

  const toggleList = (playlist) => {
    if (playlistHas(playlist, item.id)) {
      setLists(removeFromPlaylist(userId, playlist.id, item.id))
      setNote(`Removed from ${playlist.name}`)
    } else {
      setLists(addToPlaylist(userId, playlist.id, item))
      setNote(`Saved to ${playlist.name}`)
    }
  }

  const makeList = (event) => {
    event.preventDefault()
    const title = name.trim() || 'My playlist'
    const next = createPlaylist(userId, title)
    const created = next[0]
    setLists(addToPlaylist(userId, created.id, item))
    setName('')
    setNote(`Saved to ${created.name}`)
  }

  const sendReport = async (event) => {
    event.preventDefault()
    if (!requireAuth()) return
    try {
      await dispatch(normalActions.add_to_report(item.id, userId, reason.trim() || 'Report', ''))
      close()
    } catch (err) {
      window.alert(err.message || 'Could not send report')
    }
  }

  return (
    <div className="watchMoreWrap">
      {promptEl}
      <button
        type="button"
        className="watchAction"
        aria-label="More"
        onClick={event => {
          event.preventDefault()
          event.stopPropagation()
          if (open && phase !== 'out') close()
          else openMenu()
        }}
      >
        <BsThreeDotsVertical />
        <span>More</span>
      </button>
      {(open || phase !== 'off') && createPortal(
        <div className={`watchMoreOverlay${phase === 'in' ? ' isOn' : ''}${phase === 'out' ? ' isOut' : ''}`} onClick={close} role="presentation">
          <div className="watchMoreMenu" onClick={event => event.stopPropagation()}>
            <div className="watchMoreHandle" />
            {view !== 'root' ? (
              <button type="button" className="watchMoreBack" onClick={() => setView('root')}>Back</button>
            ) : null}
            {view === 'root' ? (
              <>
                <button type="button" onClick={() => { onContent?.(); setView('content') }}>Content</button>
                <button type="button" onClick={() => setView('report')}>Report</button>
                <button
                  type="button"
                  onClick={() => {
                    onHide?.(item.id)
                    close()
                  }}
                >
                  Not interested
                </button>
                <button type="button" onClick={openSave}>Save to playlist</button>
                <button
                  type="button"
                  onClick={() => {
                    onImmersive?.(true)
                    close()
                  }}
                >
                  Clear screen
                </button>
                <button type="button" onClick={() => setView('captions')}>
                  <span>Captions</span>
                  <em>{prefs.captions === 'auto' ? 'Auto' : 'Off'}</em>
                </button>
                <button type="button" onClick={() => setView('quality')}>
                  <span>Quality</span>
                  <em>{qualityLabel(prefs.quality)}</em>
                </button>
              </>
            ) : null}
            {view === 'content' ? (
              <div className="watchMoreBlock">
                <strong>{item.title || 'Video'}</strong>
                <p>{item.description || 'No description.'}</p>
              </div>
            ) : null}
            {view === 'report' ? (
              <form className="watchMoreBlock" onSubmit={sendReport}>
                <textarea value={reason} onChange={event => setReason(event.target.value)} placeholder="Why are you reporting this?" />
                <button type="submit">Send</button>
              </form>
            ) : null}
            {view === 'playlist' ? (
              <div className="watchMoreBlock">
                <strong>Save to playlist</strong>
                {lists.length ? (
                  <ul className="watchMorePlaylists">
                    {lists.map(list => (
                      <li key={list.id}>
                        <button type="button" onClick={() => toggleList(list)}>
                          <span className={`watchMoreCheck${playlistHas(list, item.id) ? ' isOn' : ''}`} />
                          <span>{list.name}</span>
                          <em>{(list.postIds || []).length}</em>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : <p>Create a playlist to save this video.</p>}
                <form className="watchMoreCreate" onSubmit={makeList}>
                  <input value={name} onChange={event => setName(event.target.value)} placeholder="Playlist name" maxLength={40} />
                  <button type="submit">Create</button>
                </form>
                {note ? <p className="watchMoreNote">{note}</p> : null}
              </div>
            ) : null}
            {view === 'captions' ? (
              <>
                <button type="button" className={prefs.captions === 'off' ? 'isOn' : ''} onClick={() => { savePref({ captions: 'off' }); setView('root') }}>Off</button>
                <button type="button" className={prefs.captions === 'auto' ? 'isOn' : ''} onClick={() => { savePref({ captions: 'auto' }); setView('root') }}>Auto generated</button>
              </>
            ) : null}
            {view === 'quality' ? QUALITY.map(value => (
              <button
                key={String(value)}
                type="button"
                className={String(prefs.quality) === String(value) ? 'isOn' : ''}
                onClick={() => { savePref({ quality: value }); setView('root') }}
              >
                {qualityLabel(value)}
              </button>
            )) : null}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default WatchMoreMenu
